import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { Icon } from "@zapier/zinnia-icons";
import { useDetails } from "../../../utils/useDetails.js";
import { useHeaderContext } from "../Header.js";
import { Tag } from "../Tag/Tag.js";
import styles from "./MobileNav.module.scss.js";
import { Button } from "../Button/Button.js";
import { LocaleSelector } from "../LocaleSelector/LocaleSelector.js";
function MobileNav(props) {
  const context = useHeaderContext();
  const { ref, isOpen, handleSummaryClick } = useDetails({
    onOpen: () => {
      document.body.style.overflow = "hidden";
      context.emitUserInteractionEvent({
        event_action: "open",
        interaction_name: "header_mobile_nav_open"
      });
    },
    onClose: () => {
      document.body.style.overflow = "";
      context.emitUserInteractionEvent({
        event_action: "close",
        interaction_name: "header_mobile_nav_close"
      });
    }
  });
  return /* @__PURE__ */ jsxs("details", { className: styles.root, ref, open: isOpen, children: [
    /* @__PURE__ */ jsxs(
      "summary",
      {
        className: styles.summary,
        "aria-label": "Menu",
        "data-testid": "mobile-nav-summary",
        onClick: handleSummaryClick,
        children: [
          /* @__PURE__ */ jsx("span", { className: styles.open, children: /* @__PURE__ */ jsx(Icon, { name: "navMenu", size: 24 }) }),
          /* @__PURE__ */ jsx("span", { className: styles.close, children: /* @__PURE__ */ jsx(Icon, { name: "formX", size: 24 }) })
        ]
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: styles.panel, children: [
      /* @__PURE__ */ jsx("nav", { className: styles.main, "aria-label": "Main site navigation", children: /* @__PURE__ */ jsx("ul", { className: styles.list, children: props.primaryNavItems.map((item) => /* @__PURE__ */ jsx("li", { className: styles.listItem, children: item.type === "NavDropdown" ? /* @__PURE__ */ jsxs("details", { className: styles.dropdown, children: [
        /* @__PURE__ */ jsxs(
          "summary",
          {
            className: `${styles.item} ${styles.dropdownSummary}`,
            children: [
              /* @__PURE__ */ jsx("span", { children: item.label }),
              /* @__PURE__ */ jsx("span", { className: styles.dropdownIndicator, children: /* @__PURE__ */ jsx(Icon, { name: "arrowSmallDown", size: 20 }) })
            ]
          }
        ),
        [
          ...item.submenu.sections,
          ...item.submenu.featuredBlocks ?? []
        ].map(
          (section) => section.type === "SubmenuList" && /* @__PURE__ */ jsxs(
            "div",
            {
              className: styles.dropdownSection,
              children: [
                /* @__PURE__ */ jsx("div", { className: styles.dropdownHeading, children: section.heading }),
                /* @__PURE__ */ jsx("ul", { className: styles.dropdownList, children: section.items.map((nestedItem) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
                  "a",
                  {
                    className: `${styles.item} ${styles.nested}`,
                    href: context.resolveURL(nestedItem.url),
                    onClick: (e) => {
                      context.emitLinkClickEvent(e, {
                        interaction_name: "header_submenu_list_item_click",
                        custom_tracking_fields: {
                          parent: item.label
                        }
                      });
                    },
                    ...nestedItem.openInNewTab && {
                      target: "_blank",
                      rel: "noreferrer"
                    },
                    children: [
                      /* @__PURE__ */ jsx("span", { children: nestedItem.label }),
                      nestedItem.tag && /* @__PURE__ */ jsx(Tag, { text: nestedItem.tag })
                    ]
                  }
                ) }, nestedItem.label)) })
              ]
            },
            section.heading
          )
        )
      ] }) : /* @__PURE__ */ jsx(
        "a",
        {
          className: styles.item,
          href: context.resolveURL(item.url),
          onClick: (e) => {
            context.emitLinkClickEvent(e, {
              interaction_name: "header_link_click"
            });
          },
          ...item.openInNewTab && {
            target: "_blank",
            rel: "noreferrer"
          },
          children: item.label
        }
      ) }, item.label)) }) }),
      /* @__PURE__ */ jsxs("div", { className: styles.footer, children: [
        props.secondaryNavItems && props.secondaryNavItems.length > 0 && /* @__PURE__ */ jsx(
          "nav",
          {
            className: styles.footerNav,
            "aria-label": "Secondary site navigation",
            children: /* @__PURE__ */ jsxs("ul", { className: styles.footerList, children: [
              props.localeSelector && /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
                LocaleSelector,
                {
                  locales: props.localeSelector.locales,
                  baseURL: props.baseURL,
                  staging: props.staging
                }
              ) }),
              props.secondaryNavItems.map((item) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
                "a",
                {
                  className: styles.footerLink,
                  href: context.resolveURL(item.url),
                  onClick: (e) => {
                    context.emitLinkClickEvent(e, {
                      interaction_name: "header_link_click"
                    });
                  },
                  ...item.openInNewTab && {
                    target: "_blank",
                    rel: "noreferrer"
                  },
                  children: [
                    item.icon && /* @__PURE__ */ jsx(Icon, { name: item.icon, size: 18 }),
                    /* @__PURE__ */ jsx("span", { children: item.label })
                  ]
                }
              ) }, item.label))
            ] })
          }
        ),
        /* @__PURE__ */ jsx("div", { className: styles.footerButtons, children: props.isLoggedIn ? /* @__PURE__ */ jsx(
          Button,
          {
            href: context.resolveURL("/app/zaps"),
            size: "large",
            fullWidth: true,
            eventPayload: {
              interaction_name: "header_my_zaps_click"
            },
            children: "My Zaps"
          }
        ) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              href: context.resolveURL("/app/login"),
              variant: "secondary",
              size: "large",
              fullWidth: true,
              eventPayload: { interaction_name: "header_log_in_click" },
              children: "Log in"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              href: context.resolveURL("/sign-up"),
              size: "large",
              fullWidth: true,
              eventPayload: { interaction_name: "header_sign_up_click" },
              children: "Sign up"
            }
          )
        ] }) })
      ] })
    ] })
  ] });
}
export {
  MobileNav
};
